import { useMemo, useState } from 'react';
import QuoteModal from '@/components/QuoteModal';
import Seo from '@/components/Seo';
import { BRAND_LOGO_URL, HERO_FIVE_STAR_IMAGE, serviceCategoryVisuals } from '@/data/servicesData';

const SERVICES_JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'CleaningService',
  name: 'BLACKLINE CLEANING',
  url: 'https://www.blackline-cleaning.co.za',
  areaServed: ['Johannesburg', 'Sandton'],
  email: 'services@blackline-cleaning.co.za',
  telephone: ['+27690579693'],
  image: BRAND_LOGO_URL,
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Johannesburg',
    addressRegion: 'Gauteng',
    addressCountry: 'ZA',
  },
  serviceArea: {
    '@type': 'AdministrativeArea',
    name: 'Johannesburg & Sandton',
  },
};

const ServicesHero = () => (
  <div className="relative min-h-[74vh] flex items-center" data-testid="services-hero-section">
    <div className="max-w-7xl mx-auto px-6 z-10 w-full">
      <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-8 lg:gap-12 items-center">
        <div className="blackline-text-card splash-heading-bar" data-testid="services-hero-card">
          <h1 className="font-oswald text-5xl sm:text-6xl lg:text-7xl font-bold uppercase leading-none text-white mb-6" data-testid="services-hero-title">
            YOUR PROPERTY
            <br />
            IS DIRTY.
            <br />
            WE FIX THAT.
          </h1>
          <p className="font-oswald text-2xl md:text-4xl font-bold uppercase text-blackline-accent" data-testid="services-hero-subtitle">
            CLEAN. COLORFUL. ALIVE.
          </p>
          <p className="font-mono text-sm md:text-base text-zinc-300 mt-8 max-w-2xl mb-6" data-testid="services-hero-description">
            Full-service cleaning, maintenance, restoration, garden care and aviation-grade support
            for properties across Johannesburg & Sandton.
          </p>
          <div className="flex flex-wrap gap-4">
            <a
              href="tel:0690579693"
              data-testid="services-hero-call-button"
              className="inline-block px-8 py-3 border-2 border-white text-white font-mono text-sm uppercase tracking-widest hover:bg-white hover:text-black transition-colors rounded-none splash-action"
            >
              Call Now
            </a>
          </div>
        </div>

        <div className="hero-media-plain" data-testid="services-hero-live-gallery">
          <img
            src={HERO_FIVE_STAR_IMAGE}
            alt="BLACKLINE 5-star rated service visual"
            loading="lazy"
            decoding="async"
            className="hero-media-plain-image"
            data-testid="services-hero-single-image"
          />
        </div>
      </div>
    </div>
  </div>
);

const ServicesFiveStar = () => (
  <div className="max-w-7xl mx-auto px-6 -mt-10 md:-mt-14 relative z-20" data-testid="services-five-star-strip">
    <div className="five-star-strip blackline-text-card splash-heading-bar flex flex-col md:flex-row md:items-center md:justify-between gap-4">
      <div>
        <p className="font-oswald text-3xl md:text-4xl uppercase text-white" data-testid="services-five-star-title">5 STAR RATED ON GOOGLE</p>
        <p className="font-mono text-sm text-zinc-300" data-testid="services-five-star-subtitle">Trusted by homeowners, body corporates, businesses and aviation teams.</p>
      </div>
      <div className="flex items-center gap-2" data-testid="services-five-star-stars" aria-label="Five star rating">
        <span className="text-amber-300 text-3xl">★</span>
        <span className="text-amber-300 text-3xl">★</span>
        <span className="text-amber-300 text-3xl">★</span>
        <span className="text-amber-300 text-3xl">★</span>
        <span className="text-amber-300 text-3xl">★</span>
        <span className="font-oswald text-3xl text-white ml-2">5.0</span>
      </div>
    </div>
  </div>
);

const ServicesVisualGrid = () => (
  <div className="max-w-7xl mx-auto px-6 mt-24 pt-20 pb-24" data-testid="services-divisions-section">
    <div className="blackline-text-card inline-block splash-heading-bar" data-testid="services-divisions-card">
      <h2 className="font-oswald text-3xl md:text-5xl font-bold uppercase text-white mb-4" data-testid="services-divisions-title">
        OUR SERVICES (VISUAL)
      </h2>
      <p className="font-mono text-sm text-blackline-accent mb-0">Aviation Cleaning, Padel Court Maintenance, Permanent Maintenance & Gardening Services.</p>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
      {serviceCategoryVisuals.map((category) => (
        <div
          key={category.id}
          className="blackline-text-card splash-heading-bar alive-division-card"
          data-testid={`service-division-${category.id}`}
        >
          <div className="aspect-[16/10] overflow-hidden border border-blackline-border mb-5">
            <img
              src={category.image}
              alt={category.title}
              className="w-full h-full object-cover before-after-image"
              loading="lazy"
              decoding="async"
              data-testid={`service-division-${category.id}-image`}
            />
          </div>
          <h3 className="font-oswald text-2xl uppercase text-white mb-5" data-testid={`service-division-${category.id}-title`}>{category.title}</h3>
          <ul className="space-y-3">
            {category.points.map((item, idx) => (
              <li key={item} className="flex gap-3" data-testid={`service-division-${category.id}-item-${idx}`}>
                <span className="text-blackline-accent">●</span>
                <span className="font-mono text-sm text-zinc-300 uppercase">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  </div>
);

const ServicesCta = () => (
  <div className="max-w-7xl mx-auto px-6 py-24">
    <div className="border border-blackline-border p-12 md:p-16 text-center blackline-text-card splash-heading-bar" data-testid="services-cta-card">
      <h2 className="font-oswald text-4xl md:text-6xl font-bold uppercase text-white mb-6" data-testid="services-cta-title">
        READY TO FIX YOUR EXTERIOR?
      </h2>
      <p className="font-mono text-sm text-zinc-400 mb-2">
        Stop reacting. Start maintaining.
      </p>
      <p className="font-oswald text-lg uppercase text-blackline-accent font-bold mb-8">
        Servicing Johannesburg & Sandton
      </p>
      <div className="flex justify-center items-center">
        <a
          href="https://wa.me/27690579693"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="services-cta-whatsapp-button"
          className="px-12 py-4 bg-blackline-accent text-black font-mono text-sm uppercase tracking-widest font-bold hover:bg-white transition-colors rounded-none splash-action"
        >
          WhatsApp Us
        </a>
      </div>
    </div>
  </div>
);

export default function Services() {
  const [showModal, setShowModal] = useState(false);
  const jsonLd = useMemo(() => SERVICES_JSON_LD, []);

  return (
    <>
      <Seo
        title="BLACKLINE CLEANING | Exterior Cleaning Johannesburg & Sandton"
        description="Exterior cleaning & pressure washing in Johannesburg & Sandton. Aviation, padel court, maintenance, waterproofing and full exterior care services."
        canonical="https://www.blackline-cleaning.co.za/services"
        ogImage={BRAND_LOGO_URL}
        jsonLd={jsonLd}
      />

      <div className="relative pt-40 md:pt-44 pb-24" data-testid="services-page">
      <QuoteModal isOpen={showModal} onClose={() => setShowModal(false)} />
      <ServicesHero />
      <ServicesFiveStar />
      <ServicesVisualGrid />
      <ServicesCta />
    </div>
    </>
  );
}